import { useMemo, useState } from 'react'
import {
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DateObject from 'react-date-object'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import DatePicker from 'react-multi-date-picker'

import { strings } from 'strings'

import { ActivityChart } from './ActivityChart'
import { ProvinceGrowthChart } from './ProvinceGrowthChart'
import { ProvinceActivityChart } from './ProvinceActivityChart'
import {
  ProvinceRoversActivityChart,
  ProvinceRoversGrowthBarChart,
  ProvinceRoversGrowthChart,
} from './ProvinceRoversChart'
import { ProvinceBasesGrowthBarChart } from './ProvinceBasesBarChart'
import { TopRoversChart } from './TopRoversChart'
import { TopDevicesChart } from './TopDevicesChart'
import {
  AnalyticsRangeContext,
  AnalyticsRangeValue,
  EPOCH_DAY_KEY,
  computeHourlySpecForDay,
  computeSpecFromKeys,
  tehranDayKeyOf,
  tehranTodayKey,
} from './analyticsShared'

function todayDateObject(): DateObject {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return new DateObject(d).convert(persian, persian_fa)
}

export function Analytics() {
  const classes = useStyles()

  const [range, setRange] = useState<[DateObject, DateObject]>(() => {
    const t = todayDateObject()
    return [t, t]
  })
  const [isAllData, setIsAllData] = useState(false)

  // Stable per-mount ISO so every "all-time" SWR key on the page matches and
  // dedupes. Recomputed on date-range/all-data changes so the cache busts when
  // the user changes the controls, but otherwise constant.
  const value: AnalyticsRangeValue = useMemo(() => {
    const allTimeToIso = new Date().toISOString()
    if (isAllData) {
      const toKey = tehranTodayKey()
      return {
        fromKey: EPOCH_DAY_KEY,
        toKey,
        isAllData: true,
        bucketSpec: computeSpecFromKeys(EPOCH_DAY_KEY, toKey),
        allTimeToIso,
      }
    }
    const fromDate = range[0].toDate()
    const toDate = range[1].toDate()
    const fromKey = tehranDayKeyOf(fromDate)
    const toKey = tehranDayKeyOf(toDate)
    // Single-day windows degrade from a 1-bucket bar to a 24-bucket hourly
    // line — both fetches and chart rendering branch on bucketSpec.granularity.
    const bucketSpec =
      fromKey === toKey
        ? computeHourlySpecForDay(fromKey)
        : computeSpecFromKeys(fromKey, toKey)
    return {
      fromKey,
      toKey,
      isAllData: false,
      bucketSpec,
      allTimeToIso,
    }
  }, [range, isAllData])

  return (
    <Paper className={classes.paper}>
      <div className={classes.toolbar}>
        <Typography className={classes.toolbarTitle}>
          {strings.date}
        </Typography>
        <div className={classes.datePickerWrap}>
          <DatePicker
            range
            calendar={persian}
            locale={persian_fa}
            digits={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
            value={range}
            onChange={(value) => {
              if (Array.isArray(value) && value.length === 2) {
                setRange(value as [DateObject, DateObject])
              }
            }}
            format="YYYY/MM/DD"
            maxDate={new Date()}
            disabled={isAllData}
            render={(value, openCalendar) => (
              <TextField
                variant="outlined"
                size="small"
                label={`${strings.from} - ${strings.to}`}
                value={isAllData ? strings.allData : (value as string)}
                onClick={isAllData ? undefined : openCalendar}
                InputProps={{ readOnly: true }}
                disabled={isAllData}
                fullWidth
              />
            )}
          />
        </div>
        {/* <FormControlLabel
          className={classes.allDataToggle}
          control={
            <Switch
              size="small"
              color="primary"
              checked={isAllData}
              onChange={(e) => setIsAllData(e.target.checked)}
            />
          }
          label={strings.allData}
        /> */}
      </div>
      <AnalyticsRangeContext.Provider value={value}>
        <div className={classes.body}>
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {strings.growth}
            </Typography>
            <div className={classes.grid}>
              <ProvinceGrowthChart />
              <ProvinceRoversGrowthChart />
            </div>
          </section>
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {strings.activity}
            </Typography>
            <div className={classes.grid}>
              <ActivityChart />
            </div>
          </section>
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {`${strings.topDevices} / ${strings.topRovers}`}
            </Typography>
            <div className={classes.grid}>
              <TopDevicesChart />
              <TopRoversChart />
            </div>
          </section>
          {/* <section className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {strings.activityByProvince}
            </Typography>
            <div className={classes.grid}>
              <ProvinceActivityChart />
              <ProvinceRoversActivityChart />
            </div>
          </section> */}
          <section className={classes.section}>
            <div className={classes.grid}>
              <ProvinceBasesGrowthBarChart />
              <ProvinceRoversGrowthBarChart />
            </div>
          </section>
        </div>
      </AnalyticsRangeContext.Provider>
    </Paper>
  )
}

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'relative',
    width: `calc(100dvw - ${theme.spacing(2)}px)`,
    height: `calc(100dvh - 48px - ${theme.spacing(3)}px)`,
    padding: theme.spacing(1),
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    direction: 'ltr',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1, 1.5),
    borderBottom: '1px solid #e0e0e0',
    flexWrap: 'wrap',
  },
  toolbarTitle: {
    fontWeight: 700,
  },
  datePickerWrap: {
    minWidth: 240,
  },
  allDataToggle: {
    marginInlineStart: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
    padding: theme.spacing(0.5, 0.5, 0.75),
    borderBottom: '1px solid #e0e0e0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    alignContent: 'start',
  },
  gridFull: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    alignContent: 'start',
  },
}))

export default Analytics
